import type { SVGProps } from 'react';

/**
 * Hand-drawn icon set.
 *
 * All strokes, all on a 24-grid, all 1.6 weight — that consistency is most of
 * what makes an icon set feel considered. Drawing them here rather than pulling
 * a package keeps the bundle small and means every glyph matches the rest of
 * the interface instead of a stranger's grid.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 24, children, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export const IconHome = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 10.5 12 4l8.5 6.5V19a1.5 1.5 0 0 1-1.5 1.5h-3.5v-6h-7v6H5A1.5 1.5 0 0 1 3.5 19z" />
  </Icon>
);

export const IconReceipt = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5.5 3.5h13v17l-2.2-1.5-2.2 1.5-2.1-1.5-2.2 1.5-2.3-1.5z" />
    <path d="M9 8.5h6M9 12.5h6" />
  </Icon>
);

export const IconPlus = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const IconChart = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </Icon>
);

export const IconSettings = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" />
  </Icon>
);

export const IconSearch = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Icon>
);

export const IconFilter = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 6h17M6.5 12h11M10 18h4" />
  </Icon>
);

export const IconChevronRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="m9.5 5 7 7-7 7" />
  </Icon>
);

export const IconChevronLeft = (p: IconProps) => (
  <Icon {...p}>
    <path d="m14.5 5-7 7 7 7" />
  </Icon>
);

export const IconChevronDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 9 7 7 7-7" />
  </Icon>
);

export const IconClose = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Icon>
);

export const IconCheck = (p: IconProps) => (
  <Icon {...p}>
    <path d="m4.5 12.5 5 5 10-11" />
  </Icon>
);

export const IconCamera = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 8.5h3l1.5-2.5h8L17.5 8.5h3v11h-17z" />
    <circle cx="12" cy="13.5" r="3.5" />
  </Icon>
);

export const IconPaperclip = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 11.5 12 19.5a4.5 4.5 0 0 1-6.4-6.4l8.2-8.2a3 3 0 0 1 4.3 4.3l-8.2 8.2a1.5 1.5 0 0 1-2.1-2.1l7.5-7.5" />
  </Icon>
);

export const IconDownload = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.5v11M7.5 10 12 14.5 16.5 10M4.5 19.5h15" />
  </Icon>
);

export const IconTrash = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 6.5h15M9.5 6.5V4.5h5v2M6.5 6.5l1 13h9l1-13M10.5 10v6M13.5 10v6" />
  </Icon>
);

export const IconUser = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="8.5" r="3.75" />
    <path d="M4.75 20c.9-3.7 3.8-5.75 7.25-5.75S18.35 16.3 19.25 20" />
  </Icon>
);

export const IconUsers = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9.5" cy="8.5" r="3.4" />
    <path d="M3 19.5c.8-3.3 3.3-5.1 6.5-5.1s5.7 1.8 6.5 5.1" />
    <path d="M16.5 5.6a3.4 3.4 0 0 1 0 6.4M18 14.8c1.7.7 2.8 2.2 3.3 4.2" />
  </Icon>
);

export const IconLink = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.3 1.3" />
    <path d="M14 10a4 4 0 0 0-5.7 0l-3 3A4 4 0 0 0 11 18.7l1.3-1.3" />
  </Icon>
);

export const IconLock = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7.75a4 4 0 0 1 8 0V10.5" />
  </Icon>
);

export const IconGlobe = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17M12 3.5c2.2 2.4 3.3 5.3 3.3 8.5S14.2 18.1 12 20.5c-2.2-2.4-3.3-5.3-3.3-8.5S9.8 5.9 12 3.5" />
  </Icon>
);

export const IconWarning = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4.5 21 20H3z" />
    <path d="M12 10v4.5M12 17.2v.1" />
  </Icon>
);

export const IconInfo = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5.5M12 7.8v.1" />
  </Icon>
);

export const IconSpark = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.5c.9 3.6 2.4 5.1 6 6-3.6.9-5.1 2.4-6 6-.9-3.6-2.4-5.1-6-6 3.6-.9 5.1-2.4 6-6Z" />
  </Icon>
);

export const IconArchive = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="4.5" width="17" height="4" rx="1.2" />
    <path d="M5.5 8.5v10a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5v-10M10 12.5h4" />
  </Icon>
);

export const IconLogOut = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14.5 4.5H6.5A1.5 1.5 0 0 0 5 6v12a1.5 1.5 0 0 0 1.5 1.5h8" />
    <path d="M15.5 8.5 19.5 12l-4 3.5M19 12h-9" />
  </Icon>
);

// --- Category glyphs --------------------------------------------------------

const CATEGORY_ICONS: Record<string, (p: IconProps) => JSX.Element> = {
  stethoscope: (p) => (
    <Icon {...p}>
      <path d="M6 3.5v5a4 4 0 0 0 8 0v-5" />
      <path d="M10 12.5v2.5a4.5 4.5 0 0 0 9 0v-1.2" />
      <circle cx="19" cy="11.5" r="2" />
    </Icon>
  ),
  tooth: (p) => (
    <Icon {...p}>
      <path d="M7.5 3.5c1.6 0 2.2.8 4.5.8s2.9-.8 4.5-.8c2 0 3 1.6 3 3.8 0 3-1.3 4-1.9 7.2-.5 2.6-.8 5-2.4 5-1.5 0-1.6-2.4-1.9-4.4-.2-1.3-.5-2.1-1.3-2.1s-1.1.8-1.3 2.1c-.3 2-.4 4.4-1.9 4.4-1.6 0-1.9-2.4-2.4-5C5.8 11.3 4.5 10.3 4.5 7.3c0-2.2 1-3.8 3-3.8Z" />
    </Icon>
  ),
  eye: (p) => (
    <Icon {...p}>
      <path d="M2.5 12S6 6.5 12 6.5 21.5 12 21.5 12 18 17.5 12 17.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  ),
  pill: (p) => (
    <Icon {...p}>
      <rect x="2.8" y="8.6" width="18.4" height="6.8" rx="3.4" transform="rotate(-45 12 12)" />
      <path d="m9 9 6 6" />
    </Icon>
  ),
  flask: (p) => (
    <Icon {...p}>
      <path d="M9.5 3.5h5M10.5 3.5v6L5 18.4a1.5 1.5 0 0 0 1.3 2.1h11.4a1.5 1.5 0 0 0 1.3-2.1L13.5 9.5v-6" />
      <path d="M7.6 14.5h8.8" />
    </Icon>
  ),
  heart: (p) => (
    <Icon {...p}>
      <path d="M12 20s-7.5-4.4-7.5-9.4A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7.5 2.6c0 5-7.5 9.4-7.5 9.4Z" />
    </Icon>
  ),
  hospital: (p) => (
    <Icon {...p}>
      <path d="M4.5 20.5v-13l7.5-4 7.5 4v13" />
      <path d="M2.5 20.5h19M12 8.5v5M9.5 11h5M9.5 20.5v-4h5v4" />
    </Icon>
  ),
  kit: (p) => (
    <Icon {...p}>
      <rect x="3" y="7.5" width="18" height="12" rx="2.4" />
      <path d="M8.5 7.5V6a1.5 1.5 0 0 1 1.5-1.5h4A1.5 1.5 0 0 1 15.5 6v1.5M12 11v5M9.5 13.5h5" />
    </Icon>
  ),
  car: (p) => (
    <Icon {...p}>
      <path d="M3.5 16v-3.2l2-5A1.5 1.5 0 0 1 6.9 6.5h10.2a1.5 1.5 0 0 1 1.4 1.3l2 5V16" />
      <path d="M3.5 16h17M6 16v2M18 16v2M5.5 12.5h13" />
    </Icon>
  ),
  shield: (p) => (
    <Icon {...p}>
      <path d="M12 3.5 19.5 6v6c0 4.3-3 7.3-7.5 8.5C7.5 19.3 4.5 16.3 4.5 12V6Z" />
      <path d="m8.8 12 2.2 2.2 4.2-4.4" />
    </Icon>
  ),
  receipt: IconReceipt,
};

/**
 * `glyph`, not `name`: `name` is a real SVG attribute, and intersecting it with
 * a nullable string quietly fights the DOM typings.
 */
export function CategoryIcon({
  glyph,
  ...props
}: IconProps & { glyph: string | null | undefined }) {
  const Glyph = CATEGORY_ICONS[glyph ?? 'receipt'] ?? IconReceipt;
  return <Glyph {...props} />;
}

/** The wordmark: a stack of stones, widest at the base. */
export function CairnMark({ size = 28, ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <g fill="currentColor">
        <ellipse cx="32" cy="49" rx="18" ry="7" />
        <ellipse cx="32" cy="36.5" rx="13.5" ry="5.6" opacity="0.85" />
        <ellipse cx="32" cy="26" rx="9.4" ry="4.5" opacity="0.7" />
        <ellipse cx="32" cy="17.5" rx="5.8" ry="3.4" opacity="0.55" />
      </g>
    </svg>
  );
}
